import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/statuts";
import { StatutBadge } from "@/components/StatutBadge";
import {
  CalendarCheck, ClipboardList, Loader2, CheckCircle2, XCircle, FileClock, Users2, Building2, TrendingUp, Navigation,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";

const PERIODS = [
  { k: "today", l: "Aujourd'hui" },
  { k: "week", l: "Cette semaine" },
  { k: "month", l: "Ce mois" },
];

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}1a` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
      </div>
      <div className="mt-2 font-heading text-3xl font-black tracking-tight text-slate-900">{value ?? 0}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [proche, setProche] = useState([]);
  const [techs, setTechs] = useState([]);
  const [ents, setEnts] = useState([]);
  const [f, setF] = useState({ technicien_id: "", installateur_id: "" });
  const isInterne = ["admin", "planification"].includes(user.role);

  useEffect(() => {
    const params = new URLSearchParams({ period });
    if (f.technicien_id) params.set("technicien_id", f.technicien_id);
    if (f.installateur_id) params.set("installateur_id", f.installateur_id);
    setData(null);
    api.get(`/dashboard?${params}`).then((r) => setData(r.data));
  }, [period, f]);

  useEffect(() => {
    if (isInterne) {
      api.post("/planning/plus-proche", { latitude: 45.7333, longitude: 4.8667, rayon_km: 25, limit: 6 }).then((r) => setProche(r.data)).catch(() => {});
      api.get("/users/techniciens").then((r) => setTechs(r.data)).catch(() => {});
      api.get("/entreprises").then((r) => setEnts(r.data)).catch(() => {});
    }
  }, [isInterne]);

  if (!data) {
    return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  const k = data.kpis;
  const trend = data.trend.map((t) => ({ ...t, jour: t.date.slice(8) + "/" + t.date.slice(5, 7) }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Tableau de bord</h1>
          <p className="text-sm text-slate-500">Bonjour {user.prenom}, voici l'activité des visites.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isInterne && (
            <>
              <select data-testid="dash-filter-tech" value={f.technicien_id} onChange={(e) => setF({ ...f, technicien_id: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs">
                <option value="">Tous techniciens</option>
                {techs.map((t) => <option key={t.id} value={t.id}>{t.prenom} {t.nom}</option>)}
              </select>
              <select data-testid="dash-filter-inst" value={f.installateur_id} onChange={(e) => setF({ ...f, installateur_id: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs">
                <option value="">Tous partenaires</option>
                {ents.map((en) => <option key={en.id} value={en.id}>{en.raison_sociale}</option>)}
              </select>
            </>
          )}
          <div className="flex rounded-lg border border-slate-200 bg-white p-1">
            {PERIODS.map((p) => (
              <button
                key={p.k}
                data-testid={`period-${p.k}`}
                onClick={() => setPeriod(p.k)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${period === p.k ? "bg-[#0A0A0A] text-white" : "text-slate-500 hover:text-slate-900"}`}
              >
                {p.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Kpi testid="kpi-aujourdhui" icon={CalendarCheck} label="Aujourd'hui" value={k.aujourd_hui} color="#3b82f6" />
        <Kpi testid="kpi-a-planifier" icon={ClipboardList} label="À planifier" value={k.a_planifier} color="#f59e0b" />
        <Kpi testid="kpi-planifiees" icon={CalendarCheck} label="Planifiées" value={k.planifiees} color="#8b5cf6" />
        <Kpi testid="kpi-en-cours" icon={Loader2} label="En cours" value={k.en_cours} color="#f97316" />
        <Kpi testid="kpi-replanifier" icon={FileClock} label="À replanifier" value={k.a_replanifier} color="#ec4899" />
        <Kpi testid="kpi-terminees" icon={CheckCircle2} label="Terminées" value={k.terminees} color="#10b981" />
        <Kpi testid="kpi-annulees" icon={XCircle} label="Annulées" value={k.annulees} color="#ef4444" />
        {isInterne && <Kpi testid="kpi-techniciens" icon={Users2} label="Techniciens actifs" value={k.techniciens_actifs} color="#14b8a6" />}
        {isInterne && <Kpi testid="kpi-installateurs" icon={Building2} label="Partenaires actifs" value={k.installateurs_actifs} color="#ec4899" />}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-8">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-slate-700">Visites — 14 derniers jours</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend} margin={{ left: -20 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="jour" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Area type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} fill="url(#g)" name="Visites" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-4">
          <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-slate-700">Répartition par statut</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.par_statut} layout="vertical" margin={{ left: 10, right: 10 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="statut" width={0} hide />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={16}>
                {data.par_statut.map((e) => <Cell key={e.code} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {isInterne && (
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2">
            <Navigation className="h-4 w-4 text-blue-600" />
            <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-slate-700">Dossiers à planifier proches de Lyon 8e</h3>
          </div>
          {proche.length === 0 ? (
            <p className="text-sm text-slate-400">Aucun dossier à proximité.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {proche.map((v) => (
                <button key={v.id} onClick={() => navigate(`/visites/${v.id}`)} className="flex w-full items-center justify-between py-2.5 text-left transition-colors duration-200 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <span className="mono text-xs font-semibold text-slate-400">{v.numero_dossier}</span>
                    <span className="text-sm font-medium text-slate-800">{v.client_nom}</span>
                    <span className="text-xs text-slate-400">{v.code_postal} {v.ville}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatutBadge code={v.statut} color={v.statut_color} label={v.statut_label} />
                    <span className="mono text-xs font-bold text-blue-600">{v.distance_km} km</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
